import { TitleResponse } from "@/api/contacts.api";
import { shouldOpenTitlePopover } from "@/utils/contact-group-helper";
import { formatNumber } from "@/utils/number-utils";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ComboBox,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from "react-aria-components";
import { AsyncListData } from "react-stately";
import { twMerge } from "tailwind-merge";
import { Checkbox } from "../generic/Checkbox";

type Props = {
  className?: string;
  value?: string | null;
  placeholder?: string;

  selectGroup?: {
    level: string | null;
    group: string | null;
  };
  list: AsyncListData<TitleResponse>;
  cachedItems?: TitleResponse[];
  query: string;
  setQuery: (query: string) => void;
  onAddPersona?: (title: string, operator: "Equal" | "Contains") => void;
  onRemovePersona?: (title: string, operator: "Equal" | "Contains") => void;
  currentRole?: { level: string | null; group: string | null };
  duplicateCheck: (data: {
    role: { level: string | null; group: string | null };
    title?: string | null;
    titleOperator?: "Equal" | "Contains";
  }) => boolean;
  selectedTitles?: Array<{ title: string; operator: "Equal" | "Contains" }>;
  mode?: "default" | "compact";
  children?: React.ReactNode;
  onPopoverOpenChange?: (isOpen: boolean) => void;
};

export function TitlePersonaSelector({
  value,
  className,
  placeholder,
  list,
  query = "",
  setQuery,
  cachedItems,
  selectGroup: selectedRole,
  onAddPersona,
  onRemovePersona,
  duplicateCheck,
  currentRole,
  mode = "default",
  children,
  selectedTitles,
  onPopoverOpenChange,
}: Props) {
  const [buttonWidth, setButtonWidth] = useState<number>(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const popoverAnchorRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 500);
  const displayItems = list.isLoading ? cachedItems : list.items;

  const handleInputChange = (text: string) => {
    setQuery(text);
    if (selectedKey !== null) setSelectedKey(null);
  };

  const handleSelectionChange = (key: string | number | null) => {
    if (!key) return;
    const title = String(key);

    if (title === "no-results") return;

    if (onAddPersona && onRemovePersona) {
      if (title === "contains-option") {
        const containsExists = isContainsPersonaExists(query);
        handleCheckboxToggle(query, containsExists, "Contains");
      } else {
        const isSelected = isPersonaExists(title);
        handleCheckboxToggle(title, isSelected, "Equal");
      }
      setSelectedKey(null);
      return;
    }

    setSelectedKey(title);
    setQuery(title);
  };

  const handleAdd = (title: string, operator: "Equal" | "Contains") => {
    if (onAddPersona) {
      onAddPersona(title, operator);
    }
  };

  const handleRemove = (title: string, operator: "Equal" | "Contains") => {
    if (onRemovePersona) {
      onRemovePersona(title, operator);
    }
  };

  const isPersonaExists = (title: string): boolean => {
    if (!currentRole) return false;
    return duplicateCheck({
      role: currentRole,
      title,
      titleOperator: "Equal",
    });
  };

  const isContainsPersonaExists = (searchQuery: string): boolean => {
    if (!currentRole) return false;
    return duplicateCheck({
      role: currentRole,
      title: searchQuery,
      titleOperator: "Contains",
    });
  };

  const handleCheckboxToggle = (
    title: string,
    isCurrentlySelected: boolean,
    operator: "Equal" | "Contains",
  ) => {
    if (isCurrentlySelected) {
      handleRemove(title, operator);
    } else {
      handleAdd(title, operator);
    }
  };

  const isPopoverOpen = shouldOpenTitlePopover(query, selectedRole ?? null, {
    group: "Alle afdelinger",
    level: "Alle roller",
    minLen: 2,
  });

  useEffect(() => {
    setQuery(value || "");
    setSelectedKey(value ?? null);
  }, [value, setQuery]);

  useEffect(() => {
    list.setFilterText(debouncedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    const update = () => {
      if (inputWrapperRef.current)
        setButtonWidth(inputWrapperRef.current.offsetWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    if (inputWrapperRef.current) ro.observe(inputWrapperRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    onPopoverOpenChange?.(isFocused && isPopoverOpen);
  }, [isFocused, isPopoverOpen, onPopoverOpenChange]);

  return (
    <div className={twMerge("relative", className)}>
      <ComboBox
        aria-label="select-title"
        inputValue={query}
        onInputChange={handleInputChange}
        selectedKey={selectedKey}
        onSelectionChange={handleSelectionChange}
        allowsCustomValue
        allowsEmptyCollection={true}
        menuTrigger="focus"
        items={displayItems}
        shouldFocusWrap
      >
        <div
          ref={popoverAnchorRef}
          className="absolute top-0 right-0 left-0 h-0"
        />
        <div
          ref={inputWrapperRef}
          className="border-theme-outline-variant relative flex min-h-20 flex-wrap items-center gap-x-3 gap-y-3 rounded-sm border p-7 transition-all duration-200"
        >
          {children}
          <Input
            ref={inputRef}
            autoComplete="off"
            style={{ outline: "none", outlineOffset: 0, boxShadow: "none" }}
            className={twMerge(
              "min-w-25 flex-1 overflow-hidden rounded-sm font-bold text-ellipsis focus:outline-none",
              "text-theme-on-background placeholder:text-theme-on-background-variant",
              "bg-transparent text-sm",
            )}
            placeholder={
              mode === "compact" && selectedTitles && selectedTitles.length > 0
                ? undefined
                : placeholder
            }
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              // If focus is moving into our own popover/listbox, re-focus the input
              const related = e.relatedTarget as HTMLElement | null;
              if (related && popoverContentRef.current?.contains(related)) {
                requestAnimationFrame(() => {
                  inputRef.current?.focus();
                });
                return;
              }
              setIsFocused(false);
            }}
          />
          {list.isLoading && (
            <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 transform">
              <Loader2 className="text-theme-on-background-variant h-4 w-4 animate-spin" />
            </div>
          )}
        </div>
        <Popover
          isOpen={isFocused && isPopoverOpen}
          onOpenChange={(open) => {
            if (!open) {
              // Only close if the input truly lost focus
              requestAnimationFrame(() => {
                if (document.activeElement !== inputRef.current) {
                  setIsFocused(false);
                }
              });
            }
          }}
          isNonModal
          aria-label="title-options"
          triggerRef={popoverAnchorRef}
          className={clsx(
            "colors-glass backdrop-blur-glass border-theme-outline-variant overflow-hidden rounded-md border shadow-lg",
            "entering:animate-in entering:fade-in entering:slide-in-from-top-2",
            "exiting:animate-out exiting:fade-out exiting:slide-out-to-top-2",
            "overflow-x-hidden",
          )}
          style={{
            width: `${buttonWidth}px`,
            minWidth: `${buttonWidth}px`,
          }}
          placement="top"
          offset={8}
        >
          <div ref={popoverContentRef}>
            <ListBox
              className={twMerge(
                "max-h-80 overflow-x-hidden overflow-y-auto px-3 py-5 outline-none",
                "transition-all duration-200",
                "entering:animate-in animate-in fade-in slide-in-from-left-2",
              )}
              autoFocus="first"
              shouldFocusWrap
            >
              {list.items.length === 0 && !list.isLoading && (
                <ListBoxItem
                  key="no-results"
                  id="no-results"
                  isDisabled
                  textValue="No titles found"
                  className="text-theme-on-background-variant cursor-default px-2 text-sm outline-none"
                >
                  <span className="text-sm font-medium">
                    Ingen titler fundet
                  </span>
                </ListBoxItem>
              )}

              {(() => {
                const hasMinQueryLength = query.length >= 2;
                const isMultiSelectMode = Boolean(onAddPersona);
                const hasMultipleResults = list.items.length > 1;
                const shouldShowContainsOption =
                  hasMinQueryLength && isMultiSelectMode && hasMultipleResults;

                if (!shouldShowContainsOption) return null;

                const containsExists = isContainsPersonaExists(query);
                return (
                  <ListBoxItem
                    key="contains-option"
                    id="contains-option"
                    textValue={`Titler der indeholder "${query}"`}
                    className="group hover:bg-theme-on-background-hover focus:bg-theme-on-background-hover flex h-11 cursor-pointer items-center gap-x-3 rounded-md px-3 font-medium transition-colors outline-none"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="pointer-events-none">
                        <Checkbox
                          size="sm"
                          className="-m-1 size-7 justify-center"
                          isSelected={containsExists}
                        />
                      </div>

                      <span className="truncate text-[11px]">
                        {" "}
                        Titler der indeholder '{query}'
                      </span>
                    </div>
                  </ListBoxItem>
                );
              })()}

              {displayItems?.map((t) => {
                const isSelected = isPersonaExists(t.title || "");
                const isMultiSelectMode = Boolean(
                  onAddPersona && onRemovePersona,
                );
                return (
                  <ListBoxItem
                    key={t.title ?? "empty"}
                    id={t.title ?? "empty"}
                    textValue={t.title || "No title"}
                    className="group hover:bg-theme-on-background-hover focus:bg-theme-on-background-hover flex h-11 w-full cursor-pointer items-center justify-between gap-x-3 rounded-md px-3 font-medium transition-colors outline-none last:pb-0"
                  >
                    <div className="flex w-full min-w-0 items-center gap-3">
                      {isMultiSelectMode && (
                        <div className="pointer-events-none">
                          <Checkbox
                            size="sm"
                            className="-m-1 size-7 justify-center"
                            isSelected={isSelected}
                          />
                        </div>
                      )}
                      <span className="flex-1 truncate overflow-hidden text-[11px]">
                        {t.title || "No title"}
                      </span>
                      <span className="shrink-0 text-[11px]">
                        {formatNumber(t.totalCount || 0)}
                      </span>
                    </div>
                  </ListBoxItem>
                );
              })}
            </ListBox>
          </div>
        </Popover>
      </ComboBox>
    </div>
  );
}
